import React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  VStack,
  SimpleGrid,
  useToast,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Text,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  useColorModeValue,
  Icon,
  Flex,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useQuery, useMutation } from 'react-query';
import { Api } from 'api';
import { getErrorMessage, FormControlFormPhoneInput } from 'core';
import { downloadBlobAsFile } from 'core/utils';
import { Trans, t } from '@lingui/macro';
import { FaUsers, FaFileExcel, FaUpload, FaTrash, FaDownload, FaEdit } from 'react-icons/fa';
import {
  CampaignQueryCacheKey,
  SaveCampaignMatchingRequest,
  UpdateCampaignMatchingRequest,
  CampaignMatchingFilterRequest,
  BulkCampaignMatchingResult,
  CampaignMatchingData,
} from 'types';
import { DataTable } from 'components';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AdminPageTitle } from '../components';

const validationSchema = yup.object({
  campaignId: yup.number().required('Kampanya seçimi zorunludur'),
  phoneNumber: yup.string().required('Telefon numarası zorunludur'),
  name: yup.string().required('İsim zorunludur'),
  govId: yup.string().optional(),
  usageLimit: yup.number().optional().min(1, 'Kullanım limiti en az 1 olmalıdır'),
});

export const AdminCampaignMatchingPage: React.FC = () => {
  const toast = useToast();
  const { isOpen: isBulkModalOpen, onOpen: onBulkModalOpen, onClose: onBulkModalClose } = useDisclosure();
  const { isOpen: isUpdateModalOpen, onOpen: onUpdateModalOpen, onClose: onUpdateModalClose } = useDisclosure();

  // Bulk upload state
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [bulkResults, setBulkResults] = React.useState<BulkCampaignMatchingResult[]>([]);

  // Search state
  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');
  const [selectedCampaignFilter, setSelectedCampaignFilter] = React.useState('');
  const [selectedBrandFilter, setSelectedBrandFilter] = React.useState<number | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);

  // Debounce search term
  React.useEffect(() => {
    setIsSearching(true);
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setIsSearching(false);
    }, 300); // Daha hızlı response için 300ms'e düşürdüm

    return () => {
      clearTimeout(timer);
      setIsSearching(false);
    };
  }, [searchTerm]);

  // Update state
  const [selectedCampaignMatchingForUpdate, setSelectedCampaignMatchingForUpdate] =
    React.useState<CampaignMatchingData | null>(null);
  const [updateUsageLimit, setUpdateUsageLimit] = React.useState<number>(0);

  // Memoized callbacks
  const handleSearchTermChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleCampaignFilterChange = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCampaignFilter(e.target.value);
  }, []);

  // Marka filtresi için localStorage'dan başlangıç değeri al ve event listener ekle
  React.useEffect(() => {
    // localStorage'dan marka seçimini al
    const savedGroupType = localStorage.getItem('selectedExpertisePointGroupType');
    if (savedGroupType) {
      const numValue = parseInt(savedGroupType, 10);
      setSelectedBrandFilter(numValue);
    }

    // AdminDrawer'dan gelen marka değişimini dinle
    const handleBrandChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { value } = customEvent.detail;
      if (value === null || value === '' || value === undefined) {
        setSelectedBrandFilter(null);
      } else {
        const numValue = parseInt(value.toString(), 10);
        setSelectedBrandFilter(numValue);
      }
    };

    // Event listener'ı ekle
    window.addEventListener('expertisePointGroupTypeChanged', handleBrandChange);

    return () => {
      window.removeEventListener('expertisePointGroupTypeChanged', handleBrandChange);
    };
  }, []);

  // Kampanya listesi getir
  const {
    data: allCampaigns,
    isLoading: isLoadingCampaigns,
    error: queryError,
  } = useQuery(
    [CampaignQueryCacheKey.GetCampaignList],
    async () => {
      const response = await Api.Campaign.getCampaignList();
      return response.data.campaigns;
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  // Marka filtresine göre kampanyaları filtrele
  const campaigns = React.useMemo(() => {
    if (!allCampaigns) return [];
    if (!selectedBrandFilter) return allCampaigns;

    return allCampaigns.filter((campaign) => campaign.groupType === selectedBrandFilter);
  }, [allCampaigns, selectedBrandFilter]);

  // Marka değiştiğinde kampanya filtresini sıfırla
  React.useEffect(() => {
    if (selectedCampaignFilter && campaigns.length > 0) {
      const selectedCampaignExists = campaigns.some((campaign) => campaign.id.toString() === selectedCampaignFilter);
      if (!selectedCampaignExists) {
        setSelectedCampaignFilter('');
      }
    }
  }, [selectedBrandFilter, campaigns, selectedCampaignFilter]);

  // Campaign matching listesi getir
  const {
    data: campaignMatchings,
    refetch: refetchMatchings,
    isFetching,
  } = useQuery(
    ['getCampaignMatchingList', debouncedSearchTerm, selectedCampaignFilter, selectedBrandFilter],
    async () => {
      const filters: CampaignMatchingFilterRequest = {};
      if (debouncedSearchTerm) filters.searchTerm = debouncedSearchTerm;
      if (selectedCampaignFilter) filters.campaignId = Number(selectedCampaignFilter);
      if (selectedBrandFilter) filters.groupType = selectedBrandFilter;

      const response = await Api.Campaign.getCampaignMatchingList(filters);
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      enabled: true,
      keepPreviousData: true, // Bu önemli: önceki veriyi tutar, input focus'unu korur
      staleTime: 0, // Veri her zaman stale kabul edilir, filtrelerde anında güncelleme için
      cacheTime: 300000, // 5 dakika cache'te tutulur
    },
  );

  const formik = useFormik({
    initialValues: {
      campaignId: '',
      phoneNumber: '',
      name: '',
      govId: '',
      usageLimit: undefined as number | undefined,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const requestData: SaveCampaignMatchingRequest = {
          campaignId: Number(values.campaignId),
          phoneNumber: values.phoneNumber,
          name: values.name,
          govId: values.govId || undefined,
          usageLimit: values.usageLimit || undefined,
        };
        await Api.Campaign.matchCampaignToUser(requestData);
        toast({
          title: 'Başarılı',
          description: 'Kampanya başarıyla eşleştirildi',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        formik.resetForm();
        refetchMatchings();
      } catch (error) {
        toast({
          title: 'Hata',
          description: getErrorMessage(error as Error, 'Kampanya eşleştirmede hata oluştu'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
  });

  // Bulk upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setBulkResults([]); // Clear previous results
    }
  };

  // Download template file
  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/Campaign-template.xlsx';
    link.download = 'kampanya-tanımlama-taslak.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast({
        title: 'Hata',
        description: 'Lütfen bir Excel dosyası seçin',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    try {
      const response = await Api.Campaign.bulkMatchCampaigns(selectedFile);
      setBulkResults(response.data || []);

      const successCount = response.data?.filter((r) => !r.errorMessage).length || 0;
      const errorCount = response.data?.filter((r) => r.errorMessage).length || 0;

      toast({
        title: 'Toplu Yükleme Tamamlandı',
        description: `${successCount} başarılı, ${errorCount} hatalı kayıt`,
        status: successCount > 0 ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Hata',
        description: getErrorMessage(error as Error, 'Toplu yükleme sırasında hata oluştu'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Export mutation
  const exportMutation = useMutation<Blob, Error>(
    () => {
      const filters: CampaignMatchingFilterRequest = {};
      if (debouncedSearchTerm) filters.searchTerm = debouncedSearchTerm;
      if (selectedCampaignFilter) filters.campaignId = Number(selectedCampaignFilter);
      if (selectedBrandFilter) filters.groupType = selectedBrandFilter;
      return Api.Campaign.exportCampaignMatchingList(filters);
    },
    {
      onSuccess: (data) => {
        const date = new Date();
        const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
          date.getDate(),
        ).padStart(2, '0')}`;
        const fileName = `Kampanya-Eslestirmeler-${formattedDate}.xlsx`;
        downloadBlobAsFile(data, fileName);

        toast({
          title: 'Başarılı',
          description: 'Excel dosyası başarıyla indirildi',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      },
      onError: (error) => {
        toast({
          title: 'Hata',
          description: getErrorMessage(error, 'Excel export sırasında hata oluştu'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  // DataTable columns for bulk results
  const bulkResultColumns: ColumnDef<BulkCampaignMatchingResult>[] = [
    {
      header: t`Telefon Numarası`,
      accessorKey: 'phoneNumber',
    },
    {
      header: t`Kampanya Kodu`,
      accessorKey: 'campaignCode',
    },
    {
      header: t`İsim`,
      accessorKey: 'name',
    },
    {
      header: t`Durum`,
      accessorKey: 'errorMessage',
      cell: (info) => {
        const errorMessage = info.getValue() as string;
        if (errorMessage) {
          // Truncate long error messages
          const truncated = errorMessage.length > 50 ? `${errorMessage.substring(0, 50)}...` : errorMessage;
          return (
            <Tooltip label={errorMessage} placement="top">
              <Badge colorScheme="red" variant="subtle" cursor="help">
                {truncated}
              </Badge>
            </Tooltip>
          );
        }
        return (
          <Badge colorScheme="green" variant="subtle">
            <Trans>Başarılı</Trans>
          </Badge>
        );
      },
    },
  ];

  // Column helper for campaign matching table
  const columnHelper = createColumnHelper<CampaignMatchingData>();

  // Delete handler
  const handleDelete = async (id: number) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Bu kampanya eşleştirmesini silmek istediğinizden emin misiniz?')) {
      try {
        await Api.Campaign.deleteCampaignMatching(id);
        toast({
          title: 'Başarılı',
          description: 'Kampanya eşleştirmesi başarıyla silindi',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        refetchMatchings();
      } catch (error) {
        toast({
          title: 'Hata',
          description: getErrorMessage(error as Error, 'Silme işleminde hata oluştu'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // Update handler
  const handleOpenUpdate = (campaignMatchingData: CampaignMatchingData) => {
    setSelectedCampaignMatchingForUpdate(campaignMatchingData);
    setUpdateUsageLimit(campaignMatchingData.usageLimit || 0);
    onUpdateModalOpen();
  };

  // Update mutation
  const updateMutation = useMutation(
    async (request: UpdateCampaignMatchingRequest) => {
      await Api.Campaign.updateCampaignMatching(request);
    },
    {
      onSuccess: () => {
        toast({
          title: 'Başarılı',
          description: 'Kampanya eşleştirmesi başarıyla güncellendi',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        refetchMatchings();
        onUpdateModalClose();
        setSelectedCampaignMatchingForUpdate(null);
      },
      onError: (error) => {
        toast({
          title: 'Hata',
          description: getErrorMessage(error as Error, 'Güncelleme işleminde hata oluştu'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      },
    },
  );

  const handleUpdate = () => {
    if (!selectedCampaignMatchingForUpdate) return;

    const currentLimit = selectedCampaignMatchingForUpdate.usageLimit || 0;
    if (updateUsageLimit < currentLimit) {
      toast({
        title: 'Hata',
        description: 'Yeni limit mevcut limitten düşük olamaz',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    updateMutation.mutate({
      id: selectedCampaignMatchingForUpdate.id,
      usageLimit: updateUsageLimit,
    });
  };

  // Campaign matching list columns
  const campaignMatchingColumns = [
    columnHelper.display({
      id: 'actions',
      header: 'İşlem',
      cell: (info) => (
        <Flex justify="center" gap={2}>
          <Icon
            as={FaEdit}
            color="blue.500"
            cursor="pointer"
            onClick={() => handleOpenUpdate(info.row.original)}
            _hover={{ color: 'blue.600' }}
            _dark={{ color: 'blue.300', _hover: { color: 'blue.200' } }}
          />
          <Icon
            as={FaTrash}
            color="red.500"
            cursor="pointer"
            onClick={() => handleDelete(info.row.original.id)}
            _hover={{ color: 'red.600' }}
            _dark={{ color: 'red.300', _hover: { color: 'red.200' } }}
          />
        </Flex>
      ),
    }),
    columnHelper.accessor('createdAt', {
      header: 'Oluşturma TARİHİ',
      cell: (info) => new Date(info.getValue()).toLocaleDateString('tr-TR') || '-',
    }),
    columnHelper.accessor('campaignName', {
      header: 'Kampanya',
    }),
    columnHelper.accessor('campaignCode', {
      header: 'Kod',
    }),
    columnHelper.accessor('discountRateStr', {
      header: 'İNDİRİM',
    }),
    columnHelper.accessor('phoneNumber', {
      header: 'Telefon',
    }),
    columnHelper.accessor('name', {
      header: 'İSİM',
    }),
    columnHelper.accessor('usageLimit', {
      header: 'LİMİT',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('campaignEndDate', {
      header: 'BİTİŞ TARİHİ',
      cell: (info) => new Date(info.getValue()).toLocaleDateString('tr-TR') || '-',
    }),
  ];

  // Server-side filtering is now handled by the query, no need for client-side filtering
  const filteredCampaignMatchings = campaignMatchings;

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <>
      <Box minH="100vh" bg={bgColor} p={6} mt={7}>
        <AdminPageTitle title={t`Kampanya Eşleştirme`} />

        <Flex direction="column" align="center" mx="auto">
          {!isLoadingCampaigns && campaigns && campaigns.length === 0 && !queryError && (
            <Card w="100%" mb={6} bg="orange.50" borderColor="orange.200" borderWidth="1px">
              <CardBody>
                <Flex align="center" gap={3}>
                  <Icon as={FaUsers} color="orange.500" boxSize={5} />
                  <Text color="orange.600" fontWeight="medium">
                    Aktif kampanya bulunamadı.
                  </Text>
                </Flex>
              </CardBody>
            </Card>
          )}

          {/* Main Form Card */}
          <Card w="100%" bg={cardBgColor} borderColor={borderColor} borderWidth="1px" shadow="lg">
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={useColorModeValue('gray.700', 'gray.200')}>
                  <Trans>Kampanya Bilgileri</Trans>
                </Heading>
                <HStack spacing={2}>
                  <Button
                    leftIcon={<Icon as={FaDownload} />}
                    colorScheme="blue"
                    size="sm"
                    onClick={handleDownloadTemplate}
                  >
                    <Trans>Örnek Dosya</Trans>
                  </Button>
                  <Button leftIcon={<Icon as={FaFileExcel} />} colorScheme="green" size="sm" onClick={onBulkModalOpen}>
                    <Trans>Excel ile Toplu Atama</Trans>
                  </Button>
                </HStack>
              </Flex>
            </CardHeader>
            <CardBody>
              <form onSubmit={formik.handleSubmit}>
                <VStack spacing={6} align="stretch">
                  {/* İlk grup: Kampanya, Telefon ve İsim */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl isRequired isInvalid={!!formik.errors.campaignId && formik.touched.campaignId}>
                      <FormLabel>
                        <Trans>Kampanya</Trans>
                      </FormLabel>
                      <Select
                        name="campaignId"
                        value={formik.values.campaignId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={isLoadingCampaigns ? t`Yükleniyor...` : t`Kampanya seçiniz`}
                        isDisabled={isLoadingCampaigns}
                      >
                        {campaigns && campaigns.length > 0 ? (
                          campaigns.map((campaign) => (
                            <option key={campaign.id} value={campaign.id.toString()}>
                              {campaign.name} - {campaign.discountRateStr}
                              İndirim
                            </option>
                          ))
                        ) : (
                          <option disabled value="">
                            Kampanya bulunamadı
                          </option>
                        )}
                      </Select>
                      {formik.errors.campaignId && formik.touched.campaignId && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {formik.errors.campaignId}
                        </Text>
                      )}
                    </FormControl>

                    <FormControlFormPhoneInput
                      label={t`Telefon Numarası`}
                      placeholder={t`Örn: 05xxxxxxxxx`}
                      name="phoneNumber"
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={!!formik.errors.phoneNumber && formik.touched.phoneNumber}
                      helperText={formik.errors.phoneNumber}
                      isRequired
                    />

                    <FormControl isRequired isInvalid={!!formik.errors.name && formik.touched.name}>
                      <FormLabel>
                        <Trans>İsim</Trans>
                      </FormLabel>
                      <Input
                        type="text"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={t`Kullanıcı adı`}
                      />
                      {formik.errors.name && formik.touched.name && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {formik.errors.name}
                        </Text>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  {/* İkinci grup: TC Kimlik ve Kullanım Limiti (2'şer) */}
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isInvalid={!!formik.errors.govId && formik.touched.govId}>
                      <FormLabel>
                        <Trans>TC Kimlik No (Opsiyonel)</Trans>
                      </FormLabel>
                      <Input
                        type="text"
                        name="govId"
                        value={formik.values.govId}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={t`TC Kimlik Numarası`}
                      />
                      {formik.errors.govId && formik.touched.govId && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {formik.errors.govId}
                        </Text>
                      )}
                    </FormControl>

                    <FormControl isInvalid={!!formik.errors.usageLimit && formik.touched.usageLimit}>
                      <FormLabel>
                        <Trans>Kullanım Limiti (Opsiyonel)</Trans>
                      </FormLabel>
                      <NumberInput
                        value={formik.values.usageLimit || ''}
                        onChange={(valueString) => {
                          formik.setFieldValue('usageLimit', valueString ? Number(valueString) : undefined);
                        }}
                        min={1}
                      >
                        <NumberInputField placeholder={t`Kullanım limiti`} />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      {formik.errors.usageLimit && formik.touched.usageLimit && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {formik.errors.usageLimit}
                        </Text>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  <HStack justify="flex-end" mt={6}>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => formik.resetForm()}
                      isDisabled={formik.isSubmitting}
                    >
                      <Trans>Temizle</Trans>
                    </Button>
                    <Button
                      type="submit"
                      colorScheme="orange"
                      isLoading={formik.isSubmitting}
                      loadingText={t`Kaydediliyor...`}
                    >
                      <Trans>Kampanya Eşleştir</Trans>
                    </Button>
                  </HStack>
                </VStack>
              </form>
            </CardBody>
          </Card>

          {/* Campaign Matching List */}
          <Card w="100%" bg={cardBgColor} borderColor={borderColor} borderWidth="1px" shadow="lg" mt={6}>
            <CardHeader>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={useColorModeValue('gray.700', 'gray.200')}>
                  <Trans>Kampanya Eşleştirmeleri</Trans>
                </Heading>
                <HStack spacing={4}>
                  <Button
                    leftIcon={<Icon as={FaDownload} />}
                    colorScheme="green"
                    size="md"
                    onClick={() => exportMutation.mutate()}
                    isLoading={exportMutation.isLoading}
                    loadingText="İndiriliyor..."
                  >
                    <Trans>Kayıtları Excel Aktar</Trans>
                  </Button>
                  <FormControl width="200px">
                    <Select
                      placeholder="Tüm Kampanyalar"
                      value={selectedCampaignFilter}
                      onChange={handleCampaignFilterChange}
                      size="md"
                    >
                      {campaigns?.map((campaign) => (
                        <option key={campaign.id} value={campaign.id.toString()}>
                          {campaign.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl width="300px">
                    <InputGroup>
                      <Input
                        placeholder="Kampanya, kod, telefon, isim ile ara..."
                        value={searchTerm}
                        onChange={handleSearchTermChange}
                        size="md"
                        autoComplete="off"
                      />
                      {(isSearching || isFetching) && (
                        <InputRightElement>
                          <Spinner size="sm" color="gray.400" />
                        </InputRightElement>
                      )}
                    </InputGroup>
                  </FormControl>
                </HStack>
              </Flex>
            </CardHeader>
            <CardBody>
              {filteredCampaignMatchings && filteredCampaignMatchings.length > 0 ? (
                <DataTable
                  data={filteredCampaignMatchings}
                  columns={campaignMatchingColumns}
                  usePagination
                  tableProps={{
                    variant: 'striped',
                    colorScheme: 'teal',
                  }}
                />
              ) : (
                <Flex justify="center" align="center" minH="200px" direction="column">
                  <Text fontSize="lg" color="gray.500" mb={2}>
                    <Trans>Kayıt bulunamadı.</Trans>
                  </Text>
                </Flex>
              )}
            </CardBody>
          </Card>
        </Flex>
      </Box>

      {/* Bulk Upload Modal */}
      <Modal isOpen={isBulkModalOpen} onClose={onBulkModalClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex align="center" gap={3}>
              <Icon as={FaFileExcel} color="green.500" />
              <Trans>Excel ile Toplu Kampanya Atama</Trans>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* File Upload Section */}
              <Box>
                <HStack justify="space-between" align="center" mb={4}>
                  <FormLabel mb={0}>
                    <Trans>Excel Dosyası Seçin</Trans>
                  </FormLabel>
                  <Button
                    leftIcon={<Icon as={FaDownload} />}
                    colorScheme="blue"
                    size="sm"
                    onClick={handleDownloadTemplate}
                  >
                    <Trans>Örnek Dosya İndir</Trans>
                  </Button>
                </HStack>
                <FormControl>
                  <Box position="relative">
                    <Input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileSelect}
                      isDisabled={isUploading}
                      opacity={0}
                      position="absolute"
                      zIndex={-1}
                      id="excel-file-input"
                    />
                    <Button
                      as="label"
                      htmlFor="excel-file-input"
                      cursor="pointer"
                      leftIcon={<Icon as={FaUpload} />}
                      variant="outline"
                      isDisabled={isUploading}
                      w="100%"
                      justifyContent="flex-start"
                    >
                      {selectedFile ? selectedFile.name : <Trans>Dosya Seçin</Trans>}
                    </Button>
                  </Box>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    <Trans>Desteklenen formatlar: .xlsx, .xls</Trans>
                  </Text>
                </FormControl>

                <HStack justify="flex-end" mt={4}>
                  <Button
                    leftIcon={<Icon as={FaUpload} />}
                    colorScheme="green"
                    onClick={handleBulkUpload}
                    isLoading={isUploading}
                    loadingText={t`Yükleniyor...`}
                    isDisabled={!selectedFile}
                  >
                    <Trans>Toplu Yükle</Trans>
                  </Button>
                </HStack>
              </Box>

              {/* Results Section */}
              {bulkResults.length > 0 && (
                <Box>
                  <Heading size="md" mb={4}>
                    <Trans>Yükleme Sonuçları</Trans>
                  </Heading>
                  <DataTable
                    columns={bulkResultColumns}
                    data={bulkResults}
                    usePagination
                    title={`${bulkResults.filter((r) => !r.errorMessage).length} başarılı, ${
                      bulkResults.filter((r) => r.errorMessage).length
                    } hatalı kayıt`}
                  />
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onBulkModalClose}>
              <Trans>Kapat</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Update Modal */}
      <Modal isOpen={isUpdateModalOpen} onClose={onUpdateModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex align="center" gap={3}>
              <Icon as={FaEdit} color="blue.500" />
              <Trans>Kampanya Eşleştirmesi Güncelle</Trans>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {selectedCampaignMatchingForUpdate && (
                <>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Kampanya:</strong> {selectedCampaignMatchingForUpdate.campaignName}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Telefon:</strong> {selectedCampaignMatchingForUpdate.phoneNumber}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>İsim:</strong> {selectedCampaignMatchingForUpdate.name}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <strong>Mevcut Limit:</strong> {selectedCampaignMatchingForUpdate.usageLimit || 'Sınırsız'}
                  </Text>

                  <FormControl isRequired>
                    <FormLabel>
                      <Trans>Yeni Kullanım Limiti</Trans>
                    </FormLabel>
                    <NumberInput
                      value={updateUsageLimit || ''}
                      onChange={(valueString) => {
                        setUpdateUsageLimit(valueString ? Number(valueString) : 0);
                      }}
                      min={selectedCampaignMatchingForUpdate.usageLimit || 0}
                    >
                      <NumberInputField placeholder={t`Yeni kullanım limiti`} />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <Text fontSize="sm" color="gray.500" mt={1}>
                      <Trans>Not: Limit sadece mevcut limitten daha yüksek bir değere güncellenebilir.</Trans>
                    </Text>
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button onClick={onUpdateModalClose}>
                <Trans>İptal</Trans>
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleUpdate}
                isLoading={updateMutation.isLoading}
                loadingText={t`Güncelleniyor...`}
              >
                <Trans>Güncelle</Trans>
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
